import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Globe, Zap, Link2, Smartphone, Lock, ChevronRight,
  AlertTriangle, CheckCircle, XCircle, Mail, ArrowRight, Shield,
  FileText, Gauge, Eye, User, LogOut
} from "lucide-react";
import ScoreGauge from "@/components/ScoreGauge";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCAN_MESSAGES = [
  { text: "Fetching page content...", icon: Globe },
  { text: "Checking meta tags & SEO...", icon: FileText },
  { text: "Analyzing page speed signals...", icon: Zap },
  { text: "Crawling for broken links...", icon: Link2 },
  { text: "Evaluating mobile responsiveness...", icon: Smartphone },
  { text: "Running security checks...", icon: Shield },
];

const FEATURE_CARDS = [
  { icon: FileText, title: "Meta Tags", desc: "Title, description, Open Graph, and structured data analysis" },
  { icon: Zap, title: "Page Speed", desc: "Render-blocking resources, script optimization, and load signals" },
  { icon: Link2, title: "Link Health", desc: "Internal link structure, canonical URLs, and heading hierarchy" },
  { icon: Smartphone, title: "Mobile Ready", desc: "Viewport settings, responsive design, and touch-friendly checks" },
];

const fadeSlide = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.08 } },
};

// ---------- Header ----------
const Header = ({ onScanClick }) => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#05030A]/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => { onScanClick(); navigate("/"); }}>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#9D00FF] flex items-center justify-center">
            <Search className="w-4 h-4 text-white" />
          </div>
          <span className="font-['Outfit'] font-bold text-base tracking-tight" data-testid="brand-name">
            Searcheverywhereoliver.com
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/pricing")}
            data-testid="header-pricing-link"
            className="px-4 py-2 text-sm font-medium text-[#A19BB0] hover:text-white transition-colors duration-200 hidden sm:block"
          >
            Pricing
          </button>
          {user ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="header-dashboard-link"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white transition-colors duration-200"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
              </button>
            </div>
          ) : (
            <button
              onClick={onScanClick}
              data-testid="header-scan-cta"
              className="px-5 py-2 text-sm font-semibold rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white transition-colors duration-200"
            >
              Scan Now
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

// ---------- Feature Card ----------
const FeatureCard = ({ icon: Icon, title, desc, index }) => (
  <motion.div
    variants={fadeSlide}
    whileHover={{ y: -4, borderColor: "rgba(255,255,255,0.15)" }}
    className="glass-card rounded-2xl p-6 sm:p-8 flex flex-col gap-4 group cursor-default"
    data-testid={`feature-card-${index}`}
  >
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F]/20 to-[#9D00FF]/20 border border-white/10 flex items-center justify-center">
      <Icon className="w-5 h-5 text-fuchsia-400" />
    </div>
    <h3 className="font-['Outfit'] text-lg font-bold tracking-tight text-white">{title}</h3>
    <p className="text-sm text-[#A19BB0] leading-relaxed">{desc}</p>
  </motion.div>
);

// ---------- Issue Row ----------
const IssueRow = ({ issue, index }) => {
  const sevClasses = {
    High: "bg-red-500/10 text-red-400 border border-red-500/20",
    Medium: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20",
    Low: "bg-green-500/10 text-green-400 border border-green-500/20",
  };
  const sevIcons = { High: XCircle, Medium: AlertTriangle, Low: CheckCircle };
  const SevIcon = sevIcons[issue.severity] || AlertTriangle;

  return (
    <motion.div
      variants={fadeSlide}
      className="flex items-start gap-4 p-4 sm:p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-colors duration-200"
      data-testid={`issue-row-${index}`}
    >
      <SevIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${issue.severity === 'High' ? 'text-red-400' : issue.severity === 'Medium' ? 'text-yellow-400' : 'text-green-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap mb-1">
          <span className="font-['Outfit'] font-semibold text-white text-sm">{issue.title}</span>
          <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full ${sevClasses[issue.severity]}`}>
            {issue.severity}
          </span>
          <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-fuchsia-400/60">{issue.category}</span>
        </div>
        <p className="text-sm text-[#A19BB0] leading-relaxed">{issue.description}</p>
      </div>
    </motion.div>
  );
};

// ========== MAIN SCANNER APP ==========
export default function ScannerApp() {
  const [appState, setAppState] = useState("input"); // input | scanning | partial | full
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanMessage, setScanMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Scanning message cycling
  useEffect(() => {
    if (appState !== "scanning") return;
    const msgInterval = setInterval(() => {
      setScanMessage((prev) => (prev + 1) % SCAN_MESSAGES.length);
    }, 2500);
    return () => clearInterval(msgInterval);
  }, [appState]);

  // Progress bar animation
  useEffect(() => {
    if (appState !== "scanning") return;
    const duration = 14000; // 14s total
    const interval = 100;
    const step = 100 / (duration / interval);
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return Math.min(prev + step + (Math.random() * step * 0.5), 100);
      });
    }, interval);
    return () => clearInterval(timer);
  }, [appState]);

  const handleScan = useCallback(async () => {
    if (!url.trim()) {
      toast.error("Please enter a URL to scan");
      return;
    }
    setAppState("scanning");
    setProgress(0);
    setScanMessage(0);
    scrollToTop();

    try {
      const res = await axios.post(`${API}/scan`, { url: url.trim() });
      // Wait for progress bar to finish visually
      const waitForProgress = () =>
        new Promise((resolve) => {
          const check = setInterval(() => {
            setProgress((curr) => {
              if (curr >= 95) {
                clearInterval(check);
                resolve();
              }
              return curr;
            });
          }, 200);
          // Failsafe - resolve after 16s max
          setTimeout(() => resolve(), 16000);
        });
      await waitForProgress();
      setScanResult(res.data);
      setAppState("partial");
      scrollToTop();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to scan the URL. Please check it and try again.";
      toast.error(msg);
      setAppState("input");
      setProgress(0);
    }
  }, [url]);

  const handleUnlock = useCallback(async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/emails`, {
        email: email.trim(),
        url: scanResult?.url || url,
        score: scanResult?.score || 0,
      });
      setAppState("full");
      scrollToTop();
      toast.success("Full report unlocked!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [email, scanResult, url]);

  const visibleCount = scanResult?.issues?.length > 3 ? 3 : Math.max(1, (scanResult?.issues?.length || 0) - 1);
  const visibleIssues = scanResult?.issues?.slice(0, visibleCount) || [];
  const hiddenIssues = scanResult?.issues?.slice(visibleCount) || [];
  const totalHidden = hiddenIssues.length;

  return (
    <div className="relative min-h-screen">
      {/* Noise */}
      <div className="noise-overlay" />

      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute -top-[30%] -right-[20%] w-[800px] h-[800px] rounded-full bg-[#9D00FF] opacity-[0.04] blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[15%] w-[600px] h-[600px] rounded-full bg-[#FF007F] opacity-[0.04] blur-[120px]" />
      </div>

      <Header onScanClick={() => { setAppState("input"); scrollToTop(); }} />

      <main className="relative z-10 pt-24 pb-20">
        <AnimatePresence mode="wait">
          {/* ===== STATE 1: INPUT ===== */}
          {appState === "input" && (
            <motion.div key="input" {...fadeSlide}>
              <div className="max-w-7xl mx-auto px-6">
                {/* Hero */}
                <div className="max-w-3xl pt-12 sm:pt-20 pb-16 sm:pb-24">
                  <motion.p
                    className="text-xs font-bold tracking-[0.25em] uppercase text-fuchsia-400 mb-6"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    Free SEO Scanner
                  </motion.p>
                  <motion.h1
                    className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Outfit'] tracking-tighter leading-[1.05] text-white mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    See exactly why your site{" "}
                    <span className="gradient-text">isn't ranking</span> — free
                  </motion.h1>
                  <motion.p
                    className="text-base sm:text-lg text-[#A19BB0] leading-relaxed max-w-xl mb-10"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Instantly audit your site for SEO issues, speed problems, broken links, and mobile friendliness. Get actionable fixes in seconds.
                  </motion.p>

                  {/* URL Input */}
                  <motion.div
                    className="flex flex-col sm:flex-row gap-3 max-w-xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="relative flex-1">
                      <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A19BB0]" />
                      <Input
                        data-testid="url-input"
                        type="url"
                        placeholder="Enter your website URL..."
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleScan()}
                        className="h-14 pl-12 pr-4 text-base bg-white/[0.04] border-white/10 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-fuchsia-500 focus-visible:border-transparent"
                      />
                    </div>
                    <button
                      data-testid="scan-button"
                      onClick={handleScan}
                      className="h-14 px-8 rounded-xl font-semibold text-base text-white bg-gradient-to-r from-[#9D00FF] to-[#FF007F] hover:opacity-90 active:scale-[0.98] transition-opacity duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      Scan My Site Free
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </motion.div>
                </div>

                {/* Feature Cards */}
                <motion.div
                  variants={stagger}
                  initial="initial"
                  animate="animate"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 pb-8"
                >
                  {FEATURE_CARDS.map((card, i) => (
                    <FeatureCard key={i} {...card} index={i} />
                  ))}
                </motion.div>

                {/* Social proof */}
                <motion.div
                  className="flex items-center gap-3 py-10 text-sm text-[#A19BB0]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="flex -space-x-2">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full border-2 border-[#05030A] bg-gradient-to-br from-fuchsia-600 to-purple-700"
                        style={{ opacity: 1 - i * 0.15 }}
                      />
                    ))}
                  </div>
                  <span>Trusted by <strong className="text-white">2,400+</strong> site owners</span>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* ===== STATE 2: SCANNING ===== */}
          {appState === "scanning" && (
            <motion.div key="scanning" {...fadeSlide}>
              <div className="max-w-2xl mx-auto px-6 pt-20 sm:pt-32 text-center">
                {/* Animated scanner */}
                <motion.div
                  className="w-24 h-24 mx-auto mb-10 rounded-2xl bg-gradient-to-br from-[#9D00FF]/20 to-[#FF007F]/20 border border-white/10 flex items-center justify-center relative"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                >
                  <Eye className="w-10 h-10 text-fuchsia-400" />
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#9D00FF] to-[#FF007F] opacity-20 animate-pulse-glow" />
                </motion.div>

                <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-3">
                  Scanning your site...
                </h2>
                <p className="text-[#A19BB0] mb-10 text-base">This usually takes about 15 seconds</p>

                {/* Progress bar */}
                <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden mb-8" data-testid="scan-progress-bar">
                  <motion.div
                    className="h-full progress-gradient rounded-full"
                    style={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                {/* Status message */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={scanMessage}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-center gap-3"
                    aria-live="polite"
                    data-testid="scan-status-message"
                  >
                    {(() => {
                      const MsgIcon = SCAN_MESSAGES[scanMessage].icon;
                      return <MsgIcon className="w-5 h-5 text-fuchsia-400" />;
                    })()}
                    <span className="text-sm font-medium text-white/70">{SCAN_MESSAGES[scanMessage].text}</span>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* ===== STATE 3: PARTIAL RESULTS ===== */}
          {appState === "partial" && scanResult && (
            <motion.div key="partial" {...fadeSlide}>
              <div className="max-w-3xl mx-auto px-6 pt-12 sm:pt-16">
                {/* Score header */}
                <div className="text-center mb-12">
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-fuchsia-400 mb-4">Scan Complete</p>
                  <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-2">
                    Your SEO Score
                  </h2>
                  <p className="text-sm text-[#A19BB0] mb-8">{scanResult.url}</p>
                  <ScoreGauge score={scanResult.score} size={200} />
                </div>

                {/* Visible issues */}
                <div className="mb-4">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-fuchsia-400 mb-6">
                    Issues Found ({scanResult.issues.length})
                  </p>
                  <motion.div variants={stagger} initial="initial" animate="animate" className="flex flex-col gap-3">
                    {visibleIssues.map((issue, i) => (
                      <IssueRow key={issue.id} issue={issue} index={i} />
                    ))}
                  </motion.div>
                </div>

                {/* Blurred / locked section */}
                {totalHidden > 0 && (
                  <div className="relative mt-6">
                    {/* Blurred preview */}
                    <div className="blur-mask">
                      <div className="flex flex-col gap-3">
                        {hiddenIssues.slice(0, 3).map((issue, i) => (
                          <IssueRow key={issue.id} issue={issue} index={visibleIssues.length + i} />
                        ))}
                      </div>
                    </div>

                    {/* Email gate overlay */}
                    <div className="absolute inset-0 flex items-center justify-center" data-testid="email-gate">
                      <div className="glass-card rounded-2xl p-6 sm:p-8 max-w-md w-full mx-4 text-center">
                        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#FF007F]/20 to-[#9D00FF]/20 border border-white/10 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-fuchsia-400" />
                        </div>
                        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-2">
                          +{totalHidden} more issues found
                        </h3>
                        <p className="text-sm text-[#A19BB0] mb-6">
                          Enter your email to unlock the full report with all issues and recommendations.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="relative flex-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A19BB0]" />
                            <Input
                              data-testid="email-input"
                              type="email"
                              placeholder="your@email.com"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
                              className="h-12 pl-10 pr-4 text-sm bg-white/[0.04] border-white/10 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-fuchsia-500"
                            />
                          </div>
                          <button
                            data-testid="unlock-button"
                            onClick={handleUnlock}
                            disabled={isSubmitting}
                            className="h-12 px-6 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#9D00FF] to-[#FF007F] hover:opacity-90 active:scale-[0.98] transition-opacity duration-200 disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                          >
                            {isSubmitting ? "Unlocking..." : "Unlock Full Report"}
                            {!isSubmitting && <ChevronRight className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ===== STATE 4: FULL REPORT ===== */}
          {appState === "full" && scanResult && (
            <motion.div key="full" {...fadeSlide}>
              <div className="max-w-3xl mx-auto px-6 pt-12 sm:pt-16">
                {/* Score header */}
                <div className="text-center mb-12">
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-fuchsia-400 mb-4">Full Report</p>
                  <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-2">
                    Your SEO Score
                  </h2>
                  <p className="text-sm text-[#A19BB0] mb-8">{scanResult.url}</p>
                  <ScoreGauge score={scanResult.score} size={200} />
                </div>

                {/* Category groups */}
                {["SEO", "Performance", "Mobile", "Security"].map((cat) => {
                  const catIssues = scanResult.issues.filter((i) => i.category === cat);
                  if (catIssues.length === 0) return null;
                  return (
                    <div key={cat} className="mb-10" data-testid={`category-${cat.toLowerCase()}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-bold tracking-[0.2em] uppercase text-fuchsia-400">
                          {cat}
                        </span>
                        <Badge className="bg-white/5 text-white/50 border-white/10 text-[10px]">
                          {catIssues.length} {catIssues.length === 1 ? 'issue' : 'issues'}
                        </Badge>
                      </div>
                      <motion.div variants={stagger} initial="initial" animate="animate" className="flex flex-col gap-3">
                        {catIssues.map((issue, i) => (
                          <IssueRow key={issue.id} issue={issue} index={i} />
                        ))}
                      </motion.div>
                    </div>
                  );
                })}

                {/* Bottom CTA */}
                <motion.div
                  className="glass-card rounded-2xl p-8 sm:p-10 text-center mt-8 mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  data-testid="weekly-scan-cta"
                >
                  <Gauge className="w-8 h-8 text-fuchsia-400 mx-auto mb-4" />
                  <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-white mb-3">
                    Want us to re-scan weekly and alert you when something breaks?
                  </h3>
                  <p className="text-sm text-[#A19BB0] mb-6 max-w-md mx-auto">
                    Stay on top of your SEO health with automated weekly scans and instant notifications.
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    data-testid="free-trial-button"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-[#9D00FF] to-[#FF007F] hover:opacity-90 active:scale-[0.98] transition-opacity duration-200"
                  >
                    Start Free Trial
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>

                {/* Rescan button */}
                <div className="text-center pb-12">
                  <button
                    data-testid="rescan-button"
                    onClick={() => {
                      setAppState("input");
                      setScanResult(null);
                      setEmail("");
                      setProgress(0);
                      scrollToTop();
                    }}
                    className="text-sm text-[#A19BB0] hover:text-white transition-colors duration-200 underline underline-offset-4"
                  >
                    Scan another site
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
